// src/services/roleService.js
import apiService from '../utils/apiInterceptor';

const roleService = {
  /**
   * Get all roles
   * @returns {Promise<Object>} Promise resolving to roles data
   */
  getAllRoles: async () => {
    try {
      return await apiService.get('/admin/role');
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  },

  /**
   * Get all admin roles
   * @returns {Promise<Object>} Promise resolving to admin roles data
   */
  getAdminRoles: async () => {
    try {
      return await apiService.get('/admin/role?is_admin=1');
    } catch (error) {
      console.error('Error fetching admin roles:', error);
      throw error;
    }
  },

  /**
   * Get all non-admin roles
   * @returns {Promise<Object>} Promise resolving to non-admin roles data
   */
  getNonAdminRoles: async () => {
    try {
      return await apiService.get('/admin/role?is_admin=0');
    } catch (error) {
      console.error('Error fetching non-admin roles:', error);
      throw error;
    }
  },

  /**
   * Get role by ID
   * @param {number|string} id - Role ID
   * @returns {Promise<Object>} Promise resolving to role data
   */
  getRoleById: async (id) => {
    try {
      return await apiService.get(`/admin/role/${id}`);
    } catch (error) {
      console.error(`Error fetching role with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new role
   * @param {Object} roleData - Role data
   * @returns {Promise<Object>} Promise resolving to created role
   */
  createRole: async (roleData) => {
    try {
      return await apiService.post('/admin/role', roleData);
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  },

  /**
   * Update a role
   * @param {number|string} id - Role ID
   * @param {Object} roleData - Updated role data
   * @returns {Promise<Object>} Promise resolving to updated role
   */
  updateRole: async (id, roleData) => {
    try {
      return await apiService.put(`/admin/role/${id}`, roleData);
    } catch (error) {
      console.error(`Error updating role with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a role
   * @param {number|string} id - Role ID to delete
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  deleteRole: async (id) => {
    try {
      return await apiService.delete(`/admin/role/${id}`);
    } catch (error) {
      console.error(`Error deleting role with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Sync permissions to role
   * @param {number|string} roleId - Role ID
   * @param {Array} permissionIds - Array of permission IDs
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  syncPermissions: async (roleId, permissionIds) => {
    try {
      return await apiService.post(`/admin/role/${roleId}/permissions`, { permissions: permissionIds });
    } catch (error) {
      console.error(`Error syncing permissions for role with ID ${roleId}:`, error);
      throw error;
    }
  },

  /**
   * Check if a role is an admin role
   * @param {number|string} roleId - Role ID
   * @returns {Promise<boolean>} Promise resolving to whether the role is admin
   */
  isAdminRole: async (roleId) => {
    try {
      const role = await roleService.getRoleById(roleId);
      return role && role.is_admin === 1;
    } catch (error) {
      console.error(`Error checking if role ${roleId} is admin:`, error);
      return false;
    }
  }
};

export default roleService;